import { alpha } from "@mui/material/styles";
import { Theme } from "@mui/system";

/**
 * Centralized box shadow definitions for the Outschool design system.
 *
 * Based on analysis of the most commonly used shadows across the website codebase.
 *
 */

export const shadows = {
  /** No shadow */
  none: "none",

  /** Subtle shadow - Use for: hover states, subtle elevation */
  50: "0px 1px 8px 0px rgba(0, 0, 0, 0.04), 0px 1px 4px 0px rgba(0, 0, 0, 0.04)",

  /** Light shadow - Use for: cards, buttons, small elevated surfaces */
  100: "0px 4px 8px 0px rgba(0, 0, 0, 0.06), 0px 0px 4px 0px rgba(0, 0, 0, 0.04)",

  /** Medium shadow - Use for: elevated cards, dropdowns, tooltips */
  200: "0px 8px 16px 0px rgba(0, 0, 0, 0.08), 0px 0px 4px 0px rgba(0, 0, 0, 0.04)",

  /** Heavy shadow - Use for: modals, popovers, drawers */
  400: "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 56px 0px rgba(0, 0, 0, 0.16)",
} as const;

export type ShadowKey = keyof typeof shadows;

/**
 * Primary-colored shadow functions for link-like elements.
 * These shadows use the primary color palette instead of neutral grey.
 * Uses the same numbering scale as the neutral shadows.
 */
export const primaryShadows = {
  /**
   * Primary-colored subtle shadow - Use for: active (pressed) states
   */
  50: (theme: Theme): string =>
    `0px 2px 6px ${alpha(
      theme.palette.primary[700],
      0.12,
    )}, 0px 0px 8px ${alpha(theme.palette.primary[700], 0.08)}`,
  /**
   * Primary-colored light shadow - Use for: hover states, interactive cards, link-like elements
   */
  100: (theme: Theme): string =>
    `0px 4px 12px ${alpha(
      theme.palette.primary[700],
      0.12,
    )}, 0px 0px 16px ${alpha(theme.palette.primary[700], 0.08)}`,
} as const;

export type PrimaryShadowKey = keyof typeof primaryShadows;
